import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonIcon from '@mui/icons-material/PersonOutline'
import SettingsIcon from '@mui/icons-material/Settings'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'

import { Transitions } from '@/components/layout/Transitions'
import useAuth from '@/hooks/useAuth'
import { RootState } from '@/store'

import { MainCard } from './MainCard'

export const ProfileSection = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation('', { keyPrefix: 'components.layout.header.profileSection' })
  const { logout } = useAuth()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)

  const driverName = useSelector((state: RootState) => state.driverSlice.driverName)

  const [selectedIndex, setSelectedIndex] = useState(-1)
  const control = false

  const handleClick = () => {
    setOpen((previousOpen) => !previousOpen)
  }

  const handleListItemClick = (index: number, route = '') => {
    setSelectedIndex(index)
    setOpen(false)

    if (route) {
      navigate(route)
    }
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event?.target as Node)) {
      return
    }
    setOpen(false)
  }

  return (
    <>
      <Chip
        color={'primary'}
        icon={
          <Avatar
            sx={{
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              background: theme.palette.primary.main,
            }}
          >
            <PersonIcon sx={{ color: theme.palette.primary.light }} />
          </Avatar>
        }
        label={<SettingsIcon color={'primary'} />}
        onClick={handleClick}
        ref={anchorRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: '8px',
          padding: '5px 0px',
          height: '45px',
          transition: 'all .2s ease-in-out',
          background: theme.palette.primary.light,
          '& .MuiChip-label': {
            lineHeight: 0,
          },
          '&[aria-controls="menu-list-grow"],&:hover': {
            borderColor: theme.palette.primary.main,
            background: theme.palette.primary.main,
            svg: {
              color: theme.palette.primary.light,
            },
          },
        }}
        variant={'filled'}
      />

      <Popper
        anchorEl={anchorRef.current}
        disablePortal
        open={open}
        placement={'bottom-start'}
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [-10, 10],
              },
            },
          ],
        }}
        role={undefined}
        transition
      >
        {({ TransitionProps }) => (
          <Transitions
            direction={'right'}
            in={open}
            position={'top'}
            type={'zoom'}
            {...TransitionProps}
          >
            <Paper elevation={2}>
              <MainCard border={false} content={false} elevation={16} shadow={theme.shadows[2]}>
                <ClickAwayListener onClickAway={handleClose}>
                  <Box>
                    <Box sx={{ p: 2 }}>
                      <Stack>
                        <Stack alignItems={'center'} direction={'row'} spacing={0.5}>
                          <Typography variant={'h4'}>{t('hi')}</Typography>
                          <Typography fontWeight={400} variant={'h4'}>
                            {driverName ?? ''}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>

                    <Box sx={{ p: 2, pt: 0 }}>
                      <Divider />
                      <List
                        component={'nav'}
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 250,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: '8px',
                          [theme.breakpoints.down('md')]: {
                            minWidth: '100%',
                          },
                          '& .MuiListItemButton-root': {
                            mt: 0.5,
                          },
                        }}
                      >
                        {control && (
                          <>
                            <ListItemButton
                              onClick={() => handleListItemClick(1, '/user-management')}
                              selected={selectedIndex === 1}
                              sx={{ borderRadius: '8px' }}
                            >
                              <ListItemIcon>
                                <SettingsIcon />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant={'body2'}>{'Kullanıcı İşlemleri'}</Typography>
                                }
                              />
                            </ListItemButton>
                            <ListItemButton
                              onClick={() => handleListItemClick(2, '/accountant-management')}
                              selected={selectedIndex === 2}
                              sx={{ borderRadius: '8px' }}
                            >
                              <ListItemIcon>
                                <SettingsIcon />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant={'body2'}>
                                    {'Muhasebeci-Şirket İşlemleri'}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                            <ListItemButton
                              onClick={() => handleListItemClick(3, '/mail-log')}
                              selected={selectedIndex === 3}
                              sx={{ borderRadius: '8px' }}
                            >
                              <ListItemIcon>
                                <SettingsIcon />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant={'body2'}>{'Mail Log Kayıtları'}</Typography>
                                }
                              />
                            </ListItemButton>
                          </>
                        )}
                        <ListItemButton
                          onClick={logout}
                          selected={selectedIndex === 4}
                          sx={{ borderRadius: '8px' }}
                        >
                          <ListItemIcon>
                            <LogoutIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant={'body2'}>{t('logout')}</Typography>}
                          />
                        </ListItemButton>
                      </List>
                    </Box>
                  </Box>
                </ClickAwayListener>
              </MainCard>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  )
}
